import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Ban, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

/**
 * WithdrawApplicationButton — Task 4.
 *
 * Renders an outline button that opens a confirm dialog and posts to
 * POST /api/talent/applications/{application_id}/withdraw.
 *
 * The parent (MyApplications.js) passes:
 *   applicationId — string, required
 *   onWithdrawn   — callback fired after a successful (or idempotent) withdraw
 *
 * Backend endpoint is already implemented in talent_withdraw_routes.py
 * and refuses to withdraw once a talent is already booked/confirmed
 * (409). This button surfaces those errors as toasts.
 */
export default function WithdrawApplicationButton({ applicationId, onWithdrawn }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const doWithdraw = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/talent/applications/${applicationId}/withdraw`);
      const msg = r?.data?.message || 'Application withdrawn.';
      toast.success(msg);
      setOpen(false);
      onWithdrawn?.(r?.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Unable to withdraw application.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="border-rose-200 text-rose-700 hover:bg-rose-50 h-8"
        data-testid={`withdraw-application-btn-${applicationId}`}
      >
        <Ban className="w-3.5 h-3.5 mr-1.5" />
        Withdraw
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="withdraw-application-dialog">
          <DialogHeader>
            <DialogTitle>Withdraw application?</DialogTitle>
            <DialogDescription>
              You'll no longer be considered for this gig. Withdrawing does
              not count as a cancellation and doesn't affect your reliability
              score. You can reapply while the gig is still open.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              data-testid="withdraw-cancel-btn"
            >
              Keep application
            </Button>
            <Button
              onClick={doWithdraw}
              disabled={busy}
              className="bg-rose-500 hover:bg-rose-600 text-white"
              data-testid="withdraw-confirm-btn"
            >
              {busy ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Withdrawing…</>
              ) : (
                <>Yes, withdraw</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
